import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage } from '@shipping/core';
import AssistantMessage from '../chat/AssistantMessage';
import ChatInput, {
  parseQuotedUserMessage,
  type ChatDraftReplacement,
  type DirectFileUploadPayload,
  type QuotedReply,
} from '../chat/ChatInput';
import QuickReplyChips from '../chat/QuickReplyChips';
import TypingIndicator from '../chat/TypingIndicator';
import { factoryButtonDraft } from '../../lib/factory-chat';
import {
  cancelFactoryRun,
  createFactoryRun,
  getFactoryRun,
  type FactoryButton,
  type FactoryConfig,
  type FactoryExpert,
  type FactoryMaterial,
  type FactoryRun,
} from '../../lib/factory-experts-api';
import { FactoryAvatar, FactoryIcon, factoryError } from './FactoryPrimitives';
import FactoryWorkbenchView from './FactoryWorkbenchView';
import { factoryWorkbenchIsConfigured } from './FactoryPublishTab';
import '../../pages/AiChatPage.css';
import './FactoryPreviewPanel.css';

type RunInput = Parameters<typeof createFactoryRun>[1];

const terminalStatuses = new Set(['completed', 'failed', 'cancelled', 'timed_out']);
const statusLabels: Record<string, string> = {
  queued: '排队中',
  preparing: '准备中',
  running: '正在执行',
  finalizing: '整理结果',
  completed: '已完成',
  failed: '执行失败',
  cancelled: '已取消',
  timed_out: '执行超时',
};

function fileToMaterial(file: File): Promise<FactoryMaterial> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      name: file.name,
      mime_type: file.type || 'application/octet-stream',
      content_base64: String(reader.result).split(',', 2)[1],
    });
    reader.onerror = () => reject(new Error(`无法读取 ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function trialMessage(
  role: 'user' | 'assistant',
  content: string,
  files: File[] = [],
): ChatMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    createdAt: new Date().toISOString(),
    ...(files.length > 0
      ? {
          attachments: files.map((file) => ({
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            fileSize: file.size,
            status: 'ready' as const,
          })),
        }
      : {}),
  };
}

function TrialUserMessage({ message }: { message: ChatMessage }) {
  const quoted = parseQuotedUserMessage(message.content);
  const visibleContent = quoted?.content ?? message.content;
  return (
    <div className="houmao-user-message-row" data-message-id={message.id}>
      <div className="houmao-user-message-stack space-y-2">
        <div className="houmao-user-message">
          {quoted ? (
            <div className="user-message-quote">
              <span>{quoted.quotedContent}</span>
            </div>
          ) : null}
          {message.attachments?.length ? (
            <div className={`fx-trial-attachments${visibleContent ? ' has-content' : ''}`}>
              {message.attachments.map((attachment, index) => (
                <span key={`${attachment.filename}-${index}`} title={attachment.filename}>
                  <FactoryIcon name="file" />
                  <span>{attachment.filename}</span>
                </span>
              ))}
            </div>
          ) : null}
          {visibleContent}
        </div>
      </div>
    </div>
  );
}

export default function FactoryPreviewPanel({
  config,
  expertId,
  ownerId,
  flush,
  action,
  initialView = 'trial',
}: {
  config: FactoryConfig;
  expertId: string;
  ownerId: string;
  flush: () => Promise<FactoryExpert>;
  action: { button: FactoryButton; nonce: number } | null;
  initialView?: 'trial' | 'workbench';
}) {
  const [view, setView] = useState(initialView);
  const [run, setRun] = useState<FactoryRun | null>(null);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pollError, setPollError] = useState('');
  const [pollNonce, setPollNonce] = useState(0);
  const [pendingInput, setPendingInput] = useState<RunInput | null>(null);
  const [draftReplacement, setDraftReplacement] = useState<ChatDraftReplacement | null>(null);
  const [quotedReply, setQuotedReply] = useState<QuotedReply | null>(null);
  const lastInput = useRef<RunInput | null>(null);
  const logged = useRef(new Set<string>());
  const session = useRef(0);
  const streamRef = useRef<HTMLDivElement>(null);
  const followBottom = useRef(true);
  const storageKey = `factory-trial:${ownerId}:${expertId}`;
  const attachmentDraftKey = `${storageKey}:composer`;
  const active = submitting || Boolean(run && !terminalStatuses.has(run.status));
  const workbenchReady = factoryWorkbenchIsConfigured(config);
  const starterQuestions = useMemo(
    () => config.starter_questions.filter((question) => question.trim()),
    [config.starter_questions],
  );

  useEffect(() => {
    if (view !== 'trial' || !followBottom.current) return;
    const stream = streamRef.current;
    if (stream) stream.scrollTop = stream.scrollHeight;
  }, [history, active, error, pollError, run, view]);

  useEffect(() => () => {
    session.current += 1;
  }, []);

  useEffect(() => {
    if (!workbenchReady && view === 'workbench') setView('trial');
  }, [view, workbenchReady]);

  useEffect(() => {
    let stale = false;
    const runId = sessionStorage.getItem(storageKey);
    if (runId) {
      void getFactoryRun(expertId, runId)
        .then((value) => {
          if (!stale) setRun(value);
        })
        .catch((issue) => {
          if (!stale) setError(`恢复试用进度失败：${factoryError(issue)}`);
        });
    }
    return () => {
      stale = true;
    };
  }, [expertId, storageKey]);

  useEffect(() => {
    if (!run || terminalStatuses.has(run.status)) return undefined;
    let stale = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const value = await getFactoryRun(expertId, run.id);
        if (stale) return;
        setRun(value);
        setPollError('');
        if (!terminalStatuses.has(value.status)) timer = setTimeout(poll, 650);
      } catch (issue) {
        if (stale) return;
        setPollError(`进度连接中断，正在自动重连：${factoryError(issue)}`);
        timer = setTimeout(poll, 1500);
      }
    };
    timer = setTimeout(poll, 150);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [expertId, run?.id, run?.status, pollNonce]);

  useEffect(() => {
    if (run?.status !== 'completed' || !run.result?.text || logged.current.has(run.id)) return;
    logged.current.add(run.id);
    setHistory((current) => [...current, trialMessage('assistant', run.result!.text!)]);
  }, [run]);

  const submit = async (input: RunInput) => {
    const epoch = session.current;
    setSubmitting(true);
    setError('');
    lastInput.current = input;
    setPendingInput(input);
    try {
      const value = await createFactoryRun(expertId, input);
      if (session.current !== epoch) return;
      setRun(value);
      setPendingInput(null);
      sessionStorage.setItem(storageKey, value.id);
    } finally {
      if (session.current === epoch) setSubmitting(false);
    }
  };

  const send = async (message: string, boundSkillId?: string, files: File[] = []) => {
    if (active || (!message.trim() && files.length === 0)) return;

    const submittedMessage = message.trim() || '请根据已上传的材料完成任务。';
    const conversation = history.map(({ role, content }) => ({
      role: role === 'assistant' ? 'assistant' as const : 'user' as const,
      content,
    }));
    const optimistic = trialMessage('user', submittedMessage, files);
    followBottom.current = true;
    setHistory((current) => [...current, optimistic]);
    setError('');

    try {
      await flush();
      const materials = await Promise.all(files.map(fileToMaterial));
      await submit({
        message: submittedMessage,
        ...(boundSkillId ? { skill_id: boundSkillId } : {}),
        idempotency_key: crypto.randomUUID(),
        materials,
        history: conversation,
      });
    } catch (issue) {
      const messageText = factoryError(issue);
      setError(messageText);
      setHistory((current) => current.map((item) => (
        item.id === optimistic.id
          ? { ...item, metadata: { ...item.metadata, failed: true, errorMessage: messageText } }
          : item
      )));
      throw issue;
    }
  };

  const handleAction = (button: FactoryButton) => {
    if (active) {
      setError('请等待当前试用完成，或先取消当前任务。');
      return;
    }
    const skill = config.skills.find((item) => item.id === button.skill_id);
    if (button.action_type === 'skill' && (!skill || !skill.enabled || skill.status !== 'ready')) {
      setError('关联技能尚未就绪，请先检查技能配置。');
      return;
    }
    try {
      setError('');
      setView('trial');
      setDraftReplacement(factoryButtonDraft(button));
    } catch (issue) {
      setError(factoryError(issue));
    }
  };
  const actionRef = useRef(handleAction);
  actionRef.current = handleAction;
  useEffect(() => {
    if (action) actionRef.current(action.button);
  }, [action]);

  const stop = async () => {
    if (!run || terminalStatuses.has(run.status)) return;
    try {
      setRun(await cancelFactoryRun(expertId, run.id));
    } catch (issue) {
      setError(factoryError(issue));
    }
  };

  const sendFiles = async (payload: DirectFileUploadPayload) => {
    await send(
      payload.message,
      payload.intent?.factorySkillId,
      payload.files.map((item) => item.file),
    );
  };

  return (
    <aside className="fx-preview fx-card" aria-label="专家试用">
      <div className="fx-tabs" role="tablist" aria-label="预览类型">
        <button type="button" role="tab" aria-selected={view === 'trial'} onClick={() => setView('trial')}>
          试用
        </button>
        {workbenchReady && <button type="button" role="tab" aria-selected={view === 'workbench'} onClick={() => setView('workbench')}>
          工作台预览
        </button>}
      </div>

      {view === 'workbench' ? (
        <div className="fx-preview-workbench">
          <FactoryWorkbenchView workbench={config.workbench} onAction={handleAction} disabled={active} />
        </div>
      ) : (
        <>
          <header className="fx-trial-contact">
            <FactoryAvatar avatar={config.avatar} name={config.display_name || '未命名专家'} />
            <div>
              <strong>{config.display_name || '未命名专家'}</strong>
              <span>对话试用 · 当前草稿</span>
            </div>
          </header>
          <div
            ref={streamRef}
            className="fx-preview-stream fx-trial-chat-stream"
            role="log"
            aria-label="试用对话消息"
            aria-live="polite"
            onScroll={(event) => {
              const stream = event.currentTarget;
              followBottom.current = stream.scrollHeight - stream.scrollTop - stream.clientHeight < 80;
            }}
          >
            {history.map((item) => (
              item.role === 'assistant' ? (
                <AssistantMessage
                  key={item.id}
                  message={item}
                  variant="houmao"
                  onQuote={(id, content) => setQuotedReply({ id, content })}
                />
              ) : (
                <TrialUserMessage key={item.id} message={item} />
              )
            ))}

            {active ? (
              <TypingIndicator
                variant="houmao"
                label={run ? statusLabels[run.status] ?? run.status : '正在提交'}
              />
            ) : null}

            {history.length === 0 && !active ? (
              <div className="houmao-quick-replies-row fx-trial-starters">
                <p className="fx-trial-welcome">发一条消息，看看智能体的回答</p>
                <QuickReplyChips
                  items={starterQuestions}
                  onSelect={(question) => setDraftReplacement({ text: question })}
                />
              </div>
            ) : null}

            {run && (run.error || run.stderr || run.result?.stderr || run.result?.files?.length) ? (
              <div className="fx-run-status" role="status">
                <span className={`fx-badge ${run.status === 'failed' || run.status === 'timed_out' ? 'red' : 'blue'}`}>
                  {statusLabels[run.status] ?? run.status}
                </span>
                {run.revision ? <small>草稿 r{run.revision}</small> : null}
                {run.error ? <p className="fx-error">{run.error}</p> : null}
                {run.stderr || run.result?.stderr ? (
                  <details>
                    <summary>执行日志</summary>
                    <pre>{run.stderr || run.result?.stderr}</pre>
                  </details>
                ) : null}
                {run.result?.files?.map((file, index) => (
                  <a
                    key={`${file.name}-${index}`}
                    className="fx-result-file"
                    href={/^(https?:\/\/|\/[^/])/.test(file.url) ? file.url : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FactoryIcon name="file" />
                    <span>{file.name}</span>
                    <span>打开</span>
                  </a>
                ))}
                {terminalStatuses.has(run.status) && run.status !== 'completed' && lastInput.current ? (
                  <button
                    className="fx-button"
                    type="button"
                    disabled={active}
                    onClick={() => {
                      void submit({
                        ...lastInput.current!,
                        idempotency_key: crypto.randomUUID(),
                      }).catch((issue) => setError(factoryError(issue)));
                    }}
                  >
                    新建一次重试
                  </button>
                ) : null}
              </div>
            ) : null}

            {pollError ? (
              <div role="alert" className="fx-error fx-poll-error">
                {pollError}
                <button
                  className="fx-link"
                  type="button"
                  onClick={() => {
                    setPollError('');
                    setPollNonce((value) => value + 1);
                  }}
                >
                  立即重连
                </button>
              </div>
            ) : null}
          </div>

          <div className="fx-preview-bottom fx-trial-chat-composer">
            {error ? <p role="alert" className="fx-error">{error}</p> : null}
            {pendingInput && !submitting ? (
              <button
                className="fx-button"
                type="button"
                onClick={() => void submit(pendingInput).catch((issue) => setError(factoryError(issue)))}
              >
                重新连接此次试用
              </button>
            ) : null}
            <ChatInput
              variant="houmao"
              attachmentDraftKey={attachmentDraftKey}
              placeholder="输入问题，试用当前草稿…"
              isStreaming={active}
              allowFileUpload
              onSend={(content, _replyToMessageId, intent) => {
                void send(content, intent?.factorySkillId).catch(() => undefined);
              }}
              onFileUpload={sendFiles}
              onStop={stop}
              enablePromptEnhancement
              draftReplacement={draftReplacement}
              onDraftReplacementHandled={() => setDraftReplacement(null)}
              quotedReply={quotedReply}
              onClearQuotedReply={() => setQuotedReply(null)}
              onRestoreQuotedReply={setQuotedReply}
            />
            <p className="fx-fine">试用消耗当前账号积分。</p>
          </div>
        </>
      )}
    </aside>
  );
}
